import React, { useState, useEffect } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Search, Stethoscope, User, Calendar, FileText, Trash2, Eye, Pill } from "lucide-react";

export default function Prescriptions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dlg, setDlg] = useState(false);
  const [viewDlg, setViewDlg] = useState(null);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    patient_name: "", patient_id_number: "", patient_phone: "",
    doctor_name: "", doctor_license: "",
    prescription_date: new Date().toISOString().slice(0, 10),
    invoice_number: "", products: [], notes: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : "";
      const [r, p] = await Promise.all([
        api.get(`/prescriptions${params}`),
        api.get("/products"),
      ]);
      setItems(r.data);
      setProducts(p.data.filter(x => x.is_pharma || x.controlled));
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addProductLine = () => setForm(f => ({ ...f, products: [...f.products, { product_id: "", product_name: "", quantity: 1, dose: "", duration: "", notes: "" }] }));
  const updProduct = (i, patch) => setForm(f => ({ ...f, products: f.products.map((p, idx) => idx === i ? { ...p, ...patch } : p) }));
  const rmProduct = (i) => setForm(f => ({ ...f, products: f.products.filter((_, idx) => idx !== i) }));

  const pickProduct = (i, pid) => {
    const p = products.find(x => x.id === pid);
    if (p) updProduct(i, { product_id: pid, product_name: p.name });
  };

  const save = async () => {
    if (!form.patient_name.trim()) return toast.error("اسم المريض مطلوب");
    if (!form.doctor_name.trim()) return toast.error("اسم الطبيب مطلوب");
    try {
      await api.post("/prescriptions", form);
      toast.success("تم حفظ الوصفة");
      setDlg(false);
      setForm({ patient_name: "", patient_id_number: "", patient_phone: "", doctor_name: "", doctor_license: "", prescription_date: new Date().toISOString().slice(0, 10), invoice_number: "", products: [], notes: "" });
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (p) => {
    if (!window.confirm(`حذف وصفة "${p.patient_name}"؟`)) return;
    try { await api.delete(`/prescriptions/${p.id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="prescriptions-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><Stethoscope className="w-6 h-6 text-primary" /> سجل الوصفات الطبية</h1>
          <p className="text-sm text-muted-foreground mt-1">سجل شامل للأدوية المراقبة والوصفات الطبية للامتثال والتدقيق</p>
        </div>
        <Button onClick={() => setDlg(true)} className="gap-2" data-testid="new-prescription-btn"><Plus className="w-4 h-4" /> وصفة جديدة</Button>
      </div>

      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="ابحث باسم المريض / الطبيب / رقم الهوية..." value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="pr-9" data-testid="search-input" />
        </div>
        <Button variant="outline" onClick={load}>بحث</Button>
      </div>

      {loading ? <div className="text-center text-muted-foreground py-12">جاري التحميل...</div> :
       items.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed rounded-lg text-muted-foreground">
          <Stethoscope className="w-16 h-16 mx-auto opacity-30 mb-3" />
          <div>لا توجد وصفات مسجّلة</div>
          <Button variant="link" onClick={() => setDlg(true)} className="mt-2">أضف الوصفة الأولى</Button>
        </div>
       ) : (
        <div className="border rounded-lg overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="text-start p-3">التاريخ</th>
                <th className="text-start p-3">المريض</th>
                <th className="text-start p-3">الطبيب</th>
                <th className="text-center p-3">الأدوية</th>
                <th className="text-center p-3">فاتورة</th>
                <th className="text-center p-3">إجراء</th>
              </tr>
            </thead>
            <tbody>
              {items.map(p => (
                <tr key={p.id} className="border-b hover:bg-muted/30" data-testid={`presc-${p.id}`}>
                  <td className="p-3 text-xs font-mono">{p.prescription_date || p.created_at?.slice(0, 10)}</td>
                  <td className="p-3">
                    <div className="font-medium">{p.patient_name}</div>
                    {p.patient_id_number && <div className="text-xs text-muted-foreground font-mono">{p.patient_id_number}</div>}
                  </td>
                  <td className="p-3">
                    <div>{p.doctor_name}</div>
                    {p.doctor_license && <div className="text-xs text-muted-foreground">ترخيص: {p.doctor_license}</div>}
                  </td>
                  <td className="p-3 text-center"><span className="px-2 py-0.5 rounded bg-primary/10 text-primary text-xs">{p.products?.length || 0}</span></td>
                  <td className="p-3 text-center text-xs font-mono">{p.invoice_number ? `#${p.invoice_number}` : "—"}</td>
                  <td className="p-3 text-center">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setViewDlg(p)}><Eye className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => del(p)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
       )}

      {/* Create dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="new-prescription-dialog">
          <DialogHeader><DialogTitle>وصفة طبية جديدة</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>اسم المريض *</Label><Input value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} data-testid="patient-name" /></div>
              <div><Label>رقم الهوية</Label><Input value={form.patient_id_number} onChange={(e) => setForm({ ...form, patient_id_number: e.target.value })} /></div>
              <div><Label>هاتف المريض</Label><Input value={form.patient_phone} onChange={(e) => setForm({ ...form, patient_phone: e.target.value })} /></div>
              <div><Label>تاريخ الوصفة</Label><Input type="date" value={form.prescription_date} onChange={(e) => setForm({ ...form, prescription_date: e.target.value })} /></div>
              <div><Label>اسم الطبيب *</Label><Input value={form.doctor_name} onChange={(e) => setForm({ ...form, doctor_name: e.target.value })} data-testid="doctor-name" /></div>
              <div><Label>رقم ترخيص الطبيب</Label><Input value={form.doctor_license} onChange={(e) => setForm({ ...form, doctor_license: e.target.value })} /></div>
              <div className="col-span-2"><Label>رقم الفاتورة (إن وُجدت)</Label><Input type="number" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} placeholder="اختياري" /></div>
            </div>

            <div className="border rounded-lg p-3 bg-muted/20">
              <div className="flex items-center justify-between mb-2">
                <div className="font-bold text-sm flex items-center gap-1"><Pill className="w-4 h-4" /> الأدوية</div>
                <Button size="sm" variant="outline" onClick={addProductLine}><Plus className="w-3 h-3 me-1" /> صنف</Button>
              </div>
              {form.products.length === 0 ? (
                <p className="text-xs text-center text-muted-foreground py-2">لم تُضف أدوية بعد</p>
              ) : form.products.map((p, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 py-2 border-b last:border-0 items-end">
                  <div className="col-span-4">
                    <Label className="text-xs">الدواء</Label>
                    <select value={p.product_id} onChange={(e) => pickProduct(i, e.target.value)} className="w-full h-9 px-2 border rounded bg-background text-sm" data-testid={`presc-product-${i}`}>
                      <option value="">— اختر —</option>
                      {products.map(x => <option key={x.id} value={x.id}>{x.name} {x.controlled ? "🔒" : ""}</option>)}
                    </select>
                  </div>
                  <div className="col-span-1"><Label className="text-xs">كمية</Label><Input type="number" value={p.quantity} onChange={(e) => updProduct(i, { quantity: +e.target.value || 0 })} className="h-9" /></div>
                  <div className="col-span-2"><Label className="text-xs">الجرعة</Label><Input value={p.dose} onChange={(e) => updProduct(i, { dose: e.target.value })} placeholder="مثلاً 500mg" className="h-9" /></div>
                  <div className="col-span-2"><Label className="text-xs">المدة</Label><Input value={p.duration} onChange={(e) => updProduct(i, { duration: e.target.value })} placeholder="7 أيام" className="h-9" /></div>
                  <div className="col-span-2"><Label className="text-xs">تعليمات</Label><Input value={p.notes} onChange={(e) => updProduct(i, { notes: e.target.value })} className="h-9" /></div>
                  <div className="col-span-1"><Button size="icon" variant="ghost" className="h-9 w-9 text-destructive" onClick={() => rmProduct(i)}><Trash2 className="w-3.5 h-3.5" /></Button></div>
                </div>
              ))}
            </div>
            <div><Label>ملاحظات</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>إلغاء</Button>
            <Button onClick={save} data-testid="save-prescription-btn">حفظ الوصفة</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewDlg} onOpenChange={(o) => !o && setViewDlg(null)}>
        <DialogContent className="max-w-2xl" data-testid="view-prescription-dialog">
          {viewDlg && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-primary" /> وصفة #{viewDlg.id.slice(0, 8)}</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3 p-3 rounded bg-muted/30 border">
                  <div><User className="w-3 h-3 inline me-1" /> <b>المريض:</b> {viewDlg.patient_name}</div>
                  <div><Calendar className="w-3 h-3 inline me-1" /> <b>التاريخ:</b> {viewDlg.prescription_date || "—"}</div>
                  <div><b>الهوية:</b> {viewDlg.patient_id_number || "—"}</div>
                  <div><b>الهاتف:</b> {viewDlg.patient_phone || "—"}</div>
                  <div><Stethoscope className="w-3 h-3 inline me-1" /> <b>الطبيب:</b> {viewDlg.doctor_name}</div>
                  <div><b>ترخيص:</b> {viewDlg.doctor_license || "—"}</div>
                </div>
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50"><tr><th className="p-2 text-start">الدواء</th><th className="p-2 text-center">كمية</th><th className="p-2 text-start">جرعة</th><th className="p-2 text-start">مدة</th></tr></thead>
                    <tbody>{(viewDlg.products || []).map((p, i) => (
                      <tr key={i} className="border-t"><td className="p-2">{p.product_name}</td><td className="p-2 text-center font-mono">{p.quantity}</td><td className="p-2">{p.dose || "—"}</td><td className="p-2">{p.duration || "—"}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
                {viewDlg.notes && <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs">💬 {viewDlg.notes}</div>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
